// src/components/StaffManagement.js
// Pantalla de gestión de usuarios staff para Admin

import React, { useState, useEffect } from 'react';
import { Edit, Mail, Search, Filter, ChevronDown, ChevronUp, Users, UserPlus, MoreHorizontal, UserMinus, UserCheck } from 'lucide-react';
import { supabase, createStaff, updateStaff, getOrgStaff, darDeBajaEnClub, reactivarEnClub, reactivarEnMyklubb, updateStaffPlatformRole, updateStaffEmail, uploadStaffImage, removeStaffImage } from '../lib/supabase';
import { leerErrorDeFuncion, mensajeParaPantalla, paraPantalla, traduccionConocida } from '../lib/services/funciones';
import { accionesStaff, coincideEstado, ETIQUETA_ESTADO, MENSAJE_CORREO_OTRA_ORG, MENSAJE_REACTIVAR_OTRA_ORG } from '../lib/staffEstado';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import { useAuth } from '../hooks/useAuth';
import { useOrganization } from '../contexts/OrganizationContext';
import { FeaturedLayout } from './layouts';
import {
  Button,
  Card,
  Badge,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from './ui/dropdown-menu';
import PhotoUploadModal from './ui/PhotoUploadModal';
import PhotoAvatarButton from './ui/PhotoAvatarButton';
import { useEntitlements } from '../contexts/EntitlementsContext';
import { UpsellModal } from './billing/Upsell';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';

const StaffManagement = () => {
  const { confirm, ConfirmDialog } = useConfirmDialog()
  const { isPlatformAdmin, staff: yo } = useAuth()
  const { org } = useOrganization()
  const orgId = org?.id || null
  const { can } = useEntitlements()
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState({ nombre_completo: '', email: '', telefono: '', rol: 'coach', crear_auth: true });
  const [creating, setCreating] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [editForm, setEditForm] = useState({ nombre_completo: '', telefono: '', email: '' });
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('todos');
  const [statusFilter, setStatusFilter] = useState('activo');
  const [authStatusFilter, setAuthStatusFilter] = useState('todos');
  const [showFilters, setShowFilters] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, staff: null, action: null });
  const [roleUpdating, setRoleUpdating] = useState({});
  const [showPhotoUpload, setShowPhotoUpload] = useState(false);
  const [selectedStaffForPhoto, setSelectedStaffForPhoto] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(null);
  const [photoUploadSuccess, setPhotoUploadSuccess] = useState(null);
  const [limitUpsell, setLimitUpsell] = useState(false);
  const createStaffGuard = useUnsavedGuard({ onClose: () => setShowCreateForm(false) });
  const editStaffGuard = useUnsavedGuard({ onClose: () => setShowEditForm(false) });

  const roles = [
    { value: 'admin', label: 'Administrador', description: 'Acceso completo al sistema' },
    { value: 'head_coach', label: 'Head Coach', description: 'Coordina entrenadores y gestiona todos los grupos asignados' },
    { value: 'manager', label: 'Manager', description: 'Gestión operativa de sus grupos' },
    { value: 'coach', label: 'Coach', description: 'Entrenador responsable de sus grupos' }
  ];

  // Lo que la pantalla ofrece en cada fila (staffEstado.js): depende del estado de la membresia, de staff.activo y de si la
  // persona tambien esta activa en otra org.
  const acciones = (member) => accionesStaff(member, { isPlatformAdmin, esYo: Boolean(yo?.id) && member?.id === yo?.id });

  useEffect(() => {
    if (orgId) loadStaff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  // Todas las filas de membresia con el club (activas y dadas de baja), no la gente que tiene al club de principal.
  const loadStaff = async () => {
    try {
      setLoading(true);
      const { data, error } = await getOrgStaff(orgId);

      if (error) throw error;

      setStaff(data || []);
      setRoleUpdating({});
    } catch (err) {
      console.error('Error loading staff:', err);
      setError('No pudimos cargar el staff del club. Recarga la página.');
    } finally {
      setLoading(false);
    }
  };

  const createAuthUser = async (staffMember) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      // Call edge function to create auth user and send invitation
      const { data, error } = await supabase.functions.invoke('create-staff-auth', {
        body: {
          staff_id: staffMember.id,
          email: staffMember.email,
          nombre_completo: staffMember.nombre_completo,
          rol: staffMember.rol
        }
      });

      if (error) throw new Error((await leerErrorDeFuncion(error, 'Intenta de nuevo.')).message);
      if (data?.error) throw new Error(paraPantalla(String(data.error), 'Intenta de nuevo.'));

      setSuccess(`Acceso creado. Se envió una invitación a ${staffMember.email}`);
      setConfirmDialog({ open: false, staff: null, action: null });
      await loadStaff();
    } catch (err) {
      console.error('Error creating auth user:', err);
      setConfirmDialog({ open: false, staff: null, action: null });
      setError('No pudimos crear el acceso: ' + mensajeParaPantalla(err, 'Intenta de nuevo.'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStaff = async (e) => {
    e.preventDefault()
    if (!can('staff')) { setLimitUpsell(true); return; }
    if (!createForm.nombre_completo.trim() || !createForm.email.trim()) return
    setCreating(true)
    setError(null)
    try {
      // Validar email duplicado en la org (la lista trae tambien a quien se dio de baja: a esa persona se le reactiva)
      const existente = staff.find((s) => s.email?.toLowerCase() === createForm.email.trim().toLowerCase())
      if (existente && acciones(existente).reactivarMembresia) {
        throw new Error(`El correo "${createForm.email}" es de ${existente.nombre_completo}, que está de baja en este club. Usa "Reactivar" en su tarjeta.`)
      }
      if (existente) {
        throw new Error(`Ya existe un staff con el correo "${createForm.email}". Usa otro correo.`)
      }

      // Advertir nombre duplicado (no bloquear)
      const nameExists = staff.some((s) => s.nombre_completo?.toLowerCase() === createForm.nombre_completo.trim().toLowerCase())
      if (nameExists) {
        const continuar = await confirm('Nombre duplicado', `Ya existe un staff con el nombre "${createForm.nombre_completo}". ¿Deseas continuar de todos modos?`)
        if (!continuar) { setCreating(false); return }
      }

      const { data: newStaff, error: createError } = await createStaff(createForm)
      if (createError) throw createError

      // Si quiere crear cuenta auth, llamar a la edge function
      if (createForm.crear_auth && newStaff?.id) {
        const { data, error: authError } = await supabase.functions.invoke('create-staff-auth', {
          body: {
            staff_id: newStaff.id,
            email: createForm.email,
            nombre_completo: createForm.nombre_completo,
            rol: createForm.rol
          }
        })
        if (authError || data?.error) {
          const motivo = authError
            ? (await leerErrorDeFuncion(authError, 'Intenta más tarde.')).message
            : paraPantalla(String(data.error), 'Intenta más tarde.')
          setSuccess(`Staff creado, pero no se pudo crear la cuenta de acceso: ${motivo} Puedes crearla después.`)
        } else if (data?.email_sent === false) {
          const detalle = paraPantalla(data?.email_error, '')
          setSuccess(`Staff creado, pero el correo de invitación no se pudo enviar${detalle ? ` (${detalle})` : ''}. Reenvíalo desde el staff cuando se resuelva el envío de correos.`)
        } else {
          setSuccess(`Staff creado y se envió invitación a ${createForm.email}`)
        }
      } else {
        setSuccess(`Staff "${createForm.nombre_completo}" creado correctamente`)
      }

      createStaffGuard.resetDirty()
      setShowCreateForm(false)
      setCreateForm({ nombre_completo: '', email: '', telefono: '', rol: 'coach', crear_auth: true })
      await loadStaff()
    } catch (err) {
      setError(mensajeParaPantalla(err, 'No se pudo crear el staff.'))
    } finally {
      setCreating(false)
    }
  }

  const handleOpenEdit = (member) => {
    setEditingStaff(member)
    setEditForm({
      nombre_completo: member.nombre_completo || '',
      telefono: member.telefono || '',
      email: member.email || '',
    })
    setEditError(null)
    editStaffGuard.resetDirty()
    setShowEditForm(true)
  }

  // Cada llamada se revisa: nunca "actualizado" si algo fallo, y si se guardo una parte se dice cual. Los errores van dentro del
  // dialogo (el aviso de la pagina queda detras).
  const handleSaveEdit = async (e) => {
    e.preventDefault()
    if (!editingStaff || !editForm.nombre_completo.trim()) return

    const nombre = editForm.nombre_completo.trim()
    const newEmail = editForm.email.trim()
    // Con la persona activa en otra org el correo es de MYKLUBB: el campo va deshabilitado y no se manda.
    const emailChanged = acciones(editingStaff).correoEditable && newEmail !== (editingStaff.email || '')
    const needsAuthEmailUpdate = emailChanged && editingStaff.usuario_creado

    // Confirmacion explicita si vamos a tocar el email de la cuenta de auth
    if (needsAuthEmailUpdate) {
      const ok = await confirm(
        'Cambiar email de acceso',
        `Esta persona tendrá que iniciar sesión con "${newEmail}". ¿Confirmas el cambio?`,
        { destructive: true }
      )
      if (!ok) return
    }

    setSaving(true)
    setEditError(null)
    try {
      const { error: updateError } = await updateStaff(editingStaff.id, {
        nombre_completo: nombre,
        telefono: editForm.telefono.trim() || null,
      })
      if (updateError) {
        setEditError(`No se guardó ningún cambio: ${mensajeParaPantalla(updateError, 'intenta de nuevo.')}`)
        return
      }

      let aviso = ''
      if (emailChanged) {
        const { data, error: emailError } = needsAuthEmailUpdate
          ? await updateStaffEmail(editingStaff.id, newEmail)
          : await updateStaff(editingStaff.id, { email: newEmail })
        if (emailError) {
          // El nombre y el telefono ya quedaron: la lista se recarga y el dialogo sigue abierto para corregir el correo (o reintentar:
          // editingStaff conserva el correo de antes, asi Guardar vuelve a mandar el nuevo).
          setEditError(emailError.code === 'FICHA_SIN_ACCESO'
            // La ficha ya tiene el correo nuevo; la cuenta no.
            ? 'Se guardaron el nombre y el teléfono. No se pudo cambiar el correo de acceso; revisa el correo de la ficha.'
            : `Se guardaron el nombre y el teléfono, pero no el correo: ${mensajeParaPantalla(emailError, 'intenta de nuevo.')}`)
          setEditingStaff((prev) => (prev ? { ...prev, nombre_completo: nombre, telefono: editForm.telefono.trim() || null } : prev))
          await loadStaff()
          return
        }
        aviso = paraPantalla(data?.message, '')
      }

      setSuccess(`Se guardaron los datos de ${nombre}.${aviso ? ` ${aviso}` : ''}`)
      editStaffGuard.resetDirty()
      setShowEditForm(false)
      await loadStaff()
    } catch (err) {
      setEditError(`No se guardó ningún cambio: ${mensajeParaPantalla(err, 'intenta de nuevo.')}`)
    } finally {
      setSaving(false)
    }
  }

  // Dar de baja EN ESTE CLUB: la membresia. Si la persona tambien trabaja en otra organizacion, ahi sigue igual.
  const handleDarDeBaja = async (member) => {
    const ok = await confirm(
      'Dar de baja en este club',
      `¿Dar de baja a ${member.nombre_completo} en este club? Ya no podrá entrar a este club. Si también está en otra organización, ahí sigue igual.`,
      { destructive: true, confirmText: 'Dar de baja' }
    )
    if (!ok) return
    setError(null)
    setSuccess(null)
    const { error: bajaError } = await darDeBajaEnClub(member.id, orgId)
    if (bajaError) {
      setError(`No pudimos dar de baja a ${member.nombre_completo}: ${mensajeParaPantalla(bajaError, 'intenta de nuevo.')}`)
      return
    }
    setSuccess(`${member.nombre_completo} ya no es parte de este club. Se puede reactivar desde su tarjeta.`)
    await loadStaff()
  }

  // Reactivar: la membresia (dado de baja) o staff.activo (desactivado en MYKLUBB, solo si no esta activo en otra org).
  const handleReactivar = async (member) => {
    const a = acciones(member)
    setError(null)
    setSuccess(null)
    const { error: reactError } = a.reactivarMembresia
      ? await reactivarEnClub(member.id, orgId)
      : await reactivarEnMyklubb(member.id)
    if (reactError) {
      setError(`No pudimos reactivar a ${member.nombre_completo}: ${mensajeParaPantalla(reactError, 'intenta de nuevo.')}`)
      return
    }
    setSuccess(a.reactivarMembresia
      ? `${member.nombre_completo} vuelve a ser parte de este club.`
      : `${member.nombre_completo} puede volver a entrar.`)
    await loadStaff()
  }

  const openCreateUserDialog = (staffMember) => {
    setConfirmDialog({ open: true, staff: staffMember, action: 'create' });
  };

  const openResendInviteDialog = (staffMember) => {
    setConfirmDialog({ open: true, staff: staffMember, action: 'resend' });
  };

  const handleConfirmAction = () => {
    if (confirmDialog.action === 'create') {
      createAuthUser(confirmDialog.staff);
    } else if (confirmDialog.action === 'resend') {
      resendInvitation(confirmDialog.staff);
    }
  };

  const formatResendErrorMessage = (err) => {
    const defaultMessage = 'No pudimos reenviar la invitación. Verifica que la cuenta exista y que la configuración de correos esté funcionando.';
    if (!err) return defaultMessage;

    const rawMessage =
      err?.context?.response?.error ||
      err?.context?.error ||
      err?.message ||
      '';

    // Lo que ya se sabe decir en espanol (p. ej. el limite de correos del proveedor) va sin el detalle tecnico en ingles.
    const conocida = traduccionConocida(rawMessage);
    if (conocida) return conocida;

    const normalized = rawMessage.toLowerCase();

    // Mensaje amigable según la causa detectada
    let friendly;
    if (normalized.includes('staff member not found')) {
      friendly = 'No encontramos a esta persona en el staff. Revisa que el correo coincida con el registrado.';
    } else if (normalized.includes('does not have an authentication user')) {
      friendly = 'Esta persona todavía no tiene acceso. Usa "Crear acceso" antes de reenviar la invitación.';
    } else if (normalized.includes('authentication user not found')) {
      friendly = 'La cuenta de acceso ya no existe. Usa "Crear acceso" y luego reenvía la invitación.';
    } else if (
      normalized.includes('no such user') ||
      normalized.includes('550') ||
      normalized.includes('user unknown') ||
      normalized.includes('mailbox') ||
      normalized.includes('recipient') ||
      normalized.includes('rebote')
    ) {
      friendly = 'El correo de destino no existe o fue rechazado por el servidor (rebote). Verifica que la dirección del staff sea una bandeja real y esté bien escrita.';
    } else if (normalized.includes('template') || normalized.includes('plantilla')) {
      friendly = 'La plantilla de correo de recuperación tiene un error de configuración. Avisa al administrador de la plataforma (Authentication → Emails → plantilla "Reset Password").';
    } else if (normalized.includes('rate limit')) {
      friendly = 'El proveedor de correo bloqueó temporalmente los envíos por límite excedido. Intenta de nuevo en unos minutos.';
    } else if (
      normalized.includes('error sending recovery email') ||
      normalized.includes('could not send') ||
      normalized.includes('smtp') ||
      normalized.includes('sending email')
    ) {
      friendly = 'No se pudo enviar el correo. Posibles causas: (1) la dirección no existe o rebotó, (2) la plantilla de correo tiene un error de configuración, o (3) el servidor SMTP está mal configurado o saturado. Revisa el correo del staff y la configuración de correo en Supabase.';
    } else {
      friendly = defaultMessage;
    }

    // Mostrar también el detalle técnico (lo que devolvió el servidor) para diagnóstico
    return rawMessage ? `${friendly} (Detalle técnico: ${rawMessage})` : friendly;
  };

  const resendInvitation = async (staffMember) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      // Call edge function to resend invitation
      const { data, error } = await supabase.functions.invoke('resend-staff-invitation', {
        body: {
          email: staffMember.email
        }
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      setSuccess(`Invitación reenviada a ${staffMember.email}`);
      setConfirmDialog({ open: false, staff: null, action: null });
    } catch (err) {
      console.error('Error resending invitation:', err);
      let errToFormat = err;
      if (err?.context && typeof err.context.json === 'function') {
        try {
          const body = await err.context.json();
          console.error('Edge Function error body:', body);
          if (body?.error || body?.details) {
            // Combinar error + details: el `error` suele ser generico y la causa
            // real ("Error sending recovery email", "550 No Such User Here", etc.)
            // viene en `details`.
            errToFormat = new Error([body.error, body.details].filter(Boolean).join(' — '));
          }
        } catch (_) {
          // El cuerpo no era JSON; conservamos el error original
        }
      }
      setError(formatResendErrorMessage(errToFormat));
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (staffId, file) => {
    try {
      setUploadingImage(staffId);
      const base64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.readAsDataURL(file);
      });

      const { data, error } = await uploadStaffImage(staffId, {
        image_base64: base64,
        filename: file.name
      });

      if (error) throw new Error(error);

      if (selectedStaffForPhoto) {
        setPhotoUploadSuccess({
          player: selectedStaffForPhoto,
          url: data?.public_url || null
        });

        setTimeout(() => {
          setPhotoUploadSuccess(null);
          setShowPhotoUpload(false);
          setSelectedStaffForPhoto(null);
        }, 2000);
      }

      await loadStaff();
    } catch (err) {
      console.error('Error uploading staff image:', err);
      if (selectedStaffForPhoto) {
        setShowPhotoUpload(false);
        setSelectedStaffForPhoto(null);
      }
      setError('Error subiendo la foto');
    } finally {
      setUploadingImage(null);
    }
  };

  const openPhotoUploadModal = (staffMember) => {
    setSelectedStaffForPhoto(staffMember);
    setShowPhotoUpload(true);
  };

  const handlePhotoFileSelect = (file) => {
    if (file && selectedStaffForPhoto) {
      handleImageUpload(selectedStaffForPhoto.id, file);
    }
  };

  const handlePhotoCameraCapture = (file) => {
    if (file && selectedStaffForPhoto) {
      handleImageUpload(selectedStaffForPhoto.id, file);
    }
  };

  const handleRemovePhoto = async (staffMember) => {
    if (!staffMember?.id) return;
    try {
      setUploadingImage(staffMember.id);
      const { error } = await removeStaffImage(staffMember.id);
      if (error) throw new Error(error);

      setPhotoUploadSuccess({
        player: staffMember,
        url: null
      });

      setTimeout(() => {
        setPhotoUploadSuccess(null);
        setShowPhotoUpload(false);
        setSelectedStaffForPhoto(null);
      }, 1500);

      await loadStaff();
    } catch (err) {
      console.error('Error eliminando foto:', err);
      setError('Error eliminando la foto');
    } finally {
      setUploadingImage(null);
    }
  };

  // El rol de la membresia con el club; nunca el valor crudo.
  const getRoleLabel = (rol) => {
    const roleObj = roles.find(r => r.value === rol);
    if (roleObj) return roleObj.label;
    if (rol === 'assistant') return 'Asistente';
    return rol ? 'Otro rol' : 'Sin rol';
  };

  const getRoleBadgeVariant = (rol) => {
    switch (rol) {
      case 'admin': return 'error';
      case 'head_coach': return 'default';
      case 'coach': return 'primary';
      case 'manager': return 'success';
      case 'assistant': return 'secondary';
      default: return 'secondary';
    }
  };

  // Quien fundo la org: lo protege la base en su org (trg_protect_founder, _protect_founder_membership); ver staffEstado.js.

    const handlePlatformRoleChange = async (member, newRole) => {
      if (member.rol === newRole) return;
      try {
        setRoleUpdating(prev => ({ ...prev, [member.id]: true }));
        setError(null);
        setSuccess(null);

        const { error } = await updateStaffPlatformRole(member.id, newRole);
        if (error) throw error;

        let refreshMessage = '';
        try {
          // get_org_staff no trae auth_user_id: "soy yo" es la misma ficha de staff.
          if (yo?.id && member.id === yo.id) {
            await supabase.auth.refreshSession();
            refreshMessage = 'Tu sesión se actualizó automáticamente.';
          } else {
            refreshMessage = 'Si esta persona tiene la sesión abierta, debe salir y volver a entrar para ver los cambios.';
          }
        } catch (refreshErr) {
          refreshMessage = 'Si esta persona tiene la sesión abierta, debe salir y volver a entrar para ver los cambios.';
        }

        setSuccess(`Rol actualizado a ${getRoleLabel(newRole)} para ${member.nombre_completo}. ${refreshMessage}`);
        await loadStaff();
      } catch (err) {
        console.error('Error updating staff role:', err);
        setError(mensajeParaPantalla(err, 'No pudimos actualizar el rol.'));
      } finally {
      setRoleUpdating(prev => ({ ...prev, [member.id]: false }));
    }
  };

  if (loading && staff.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Filtered staff based on search and filters
  const filteredStaff = staff.filter(member => {
    const matchesSearch =
      member.nombre_completo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.telefono?.includes(searchTerm);

    const matchesRole = roleFilter === 'todos' || member.rol === roleFilter;
    const matchesStatus = coincideEstado(member, statusFilter);

    const matchesAuthStatus = authStatusFilter === 'todos' ||
      (authStatusFilter === 'con_usuario' && member.usuario_creado) ||
      (authStatusFilter === 'sin_usuario' && !member.usuario_creado);

    return matchesSearch && matchesRole && matchesStatus && matchesAuthStatus;
  });

  // Count active filters
  const getActiveFiltersCount = () => {
    let count = 0;
    if (roleFilter !== 'todos') count++;
    if (statusFilter !== 'todos' && statusFilter !== 'activo') count++;
    if (authStatusFilter !== 'todos') count++;
    return count;
  };

  return (
    <FeaturedLayout
      title="Gestión de Staff"
      subtitle="Gestiona acceso de usuarios al sistema"
      refreshAction={loadStaff}
    >

      {/* Search and Filter Controls */}
        <div className="mb-6">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[220px] relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                placeholder="Buscar por nombre, email o teléfono..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex-shrink-0 hidden sm:block">
            <span className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{filteredStaff.length}</span> miembros
            </span>
          </div>
            <div className="flex-shrink-0 w-full sm:w-auto">
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className="w-full sm:w-auto"
              >
                <Filter size={16} />
                <span className="hidden sm:inline">
                  Filtros {getActiveFiltersCount() > 0 && `(${getActiveFiltersCount()})`}
                </span>
              {showFilters ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </Button>
          </div>
          <Button onClick={() => { createStaffGuard.resetDirty(); setShowCreateForm(true); }}>
            <UserPlus size={16} className="mr-2" /> Nuevo Staff
          </Button>
        </div>
      </div>

      {/* Collapsible Filters */}
      {showFilters && (
        <Card className="mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-2">
                Filtrar por Rol
              </label>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Todos los roles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos los roles</SelectItem>
                  {roles.map(role => (
                    <SelectItem key={role.value} value={role.value}>
                      {role.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-2">
                Estado (por defecto: activos)
              </label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="activo">{ETIQUETA_ESTADO.activo} (por defecto)</SelectItem>
                  <SelectItem value="baja">{ETIQUETA_ESTADO.baja}</SelectItem>
                  <SelectItem value="desactivado">{ETIQUETA_ESTADO.desactivado}</SelectItem>
                  <SelectItem value="todos">Todos los estados</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-2">
                Usuario de Autenticación
              </label>
              <Select value={authStatusFilter} onValueChange={setAuthStatusFilter}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="con_usuario">Con usuario</SelectItem>
                  <SelectItem value="sin_usuario">Sin usuario</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>
      )}

      {error && (
        <div className="mb-4 px-4 py-3 bg-destructive/10 border border-destructive/20 rounded-lg flex justify-between items-center">
          <p className="text-sm text-destructive">{error}</p>
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-destructive" onClick={() => setError(null)}>×</Button>
        </div>
      )}

      {success && (
        <div className="mb-4 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-lg flex justify-between items-center">
          <p className="text-sm text-emerald-700">{success}</p>
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-emerald-600" onClick={() => setSuccess(null)}>×</Button>
        </div>
      )}

      {/* Staff List */}
      {filteredStaff.length === 0 ? (
        <div className="text-center py-16">
          <Users className="mx-auto text-muted-foreground mb-6" size={64} />
          <p className="text-muted-foreground font-medium text-lg mb-2">
            {staff.length === 0 ? 'No hay staff registrado' : 'No se encontraron miembros'}
          </p>
          <p className="text-muted-foreground text-sm">
            {staff.length === 0
              ? 'Comienza agregando tu primer miembro del staff'
              : 'Intenta con otros criterios de búsqueda'
            }
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filteredStaff.map((member) => {
            const a = acciones(member);
            return (
            <Card key={member.id} className="p-5 hover:shadow-md transition-shadow min-w-0" data-testid={`staff-${member.id}`}>
              <div className="space-y-3">
                {/* Top: Primary Information */}
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <PhotoAvatarButton
                      imageUrl={member.imagen_url}
                      name={member.nombre_completo}
                      size="md"
                      onClick={() => openPhotoUploadModal(member)}
                      disabled={!a.cambiarFoto}
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="text-black font-semibold text-sm truncate">
                        {member.nombre_completo}
                      </h3>

                      <div className="flex flex-wrap items-center gap-2 min-w-0">
                        <p className="text-muted-foreground text-xs truncate">{member.email}</p>
                        {member.telefono && <p className="text-muted-foreground text-xs">· {member.telefono}</p>}
                      </div>
                    </div>
                  </div>

                  {/* Right: Actions — 1 primaria contextual + Editar + menu (los destructivos en el menu, en rojo) */}
                  {(() => {
                    const hayEnMenu = a.reenviarInvitacion || a.darDeBaja;

                    return (
                      <div className="flex items-center gap-2 sm:justify-end sm:pt-1">
                        {(a.reactivarMembresia || a.reactivarEnMyklubb) && (
                          <Button size="sm" className="h-8 text-xs" onClick={() => handleReactivar(member)} disabled={loading}>
                            <UserCheck className="h-3 w-3 mr-1" /> Reactivar
                          </Button>
                        )}
                        {/* Sin acceso creado = lo pendiente de esta fila, por eso es la primaria */}
                        {a.crearAcceso && (
                          <Button
                            size="sm"
                            className="h-8 text-xs"
                            onClick={() => openCreateUserDialog(member)}
                            disabled={loading}
                          >
                            <UserPlus className="h-3 w-3 mr-1" /> Crear acceso
                          </Button>
                        )}
                        {a.editar && (
                          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => handleOpenEdit(member)}>
                            <Edit className="h-3 w-3 mr-1" /> Editar
                          </Button>
                        )}
                        {hayEnMenu && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 shrink-0" title="Más acciones" aria-label="Más acciones">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {a.reenviarInvitacion && (
                                <DropdownMenuItem onClick={() => openResendInviteDialog(member)} disabled={loading}>
                                  <Mail className="h-4 w-4" />
                                  Reenviar invitación
                                </DropdownMenuItem>
                              )}
                              {a.darDeBaja && a.reenviarInvitacion && <DropdownMenuSeparator />}
                              {a.darDeBaja && (
                                <DropdownMenuItem
                                  onClick={() => handleDarDeBaja(member)}
                                  className="text-destructive focus:text-destructive"
                                >
                                  <UserMinus className="h-4 w-4" />
                                  Dar de baja en este club
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {/* Bottom: Secondary Information (Tags + Role selector) */}
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <Badge variant={getRoleBadgeVariant(member.rol)} size="xs">
                    {getRoleLabel(member.rol)}
                  </Badge>
                  <Badge
                    variant={a.estado === 'activo' ? 'success' : a.estado === 'baja' ? 'secondary' : 'error'}
                    size="xs"
                  >
                    {ETIQUETA_ESTADO[a.estado]}
                  </Badge>
                  {member.usuario_creado ? (
                    <span className="text-green-600 text-xs">OK Configurado</span>
                  ) : (
                    <span className="text-orange-600 text-xs">Pendiente</span>
                  )}
                </div>

                {/* Role selector */}
                <div className="border-t pt-3">
                  {a.fundadorAqui && (
                    <p className="text-[11px] text-muted-foreground mb-1">
                      {isPlatformAdmin
                        ? 'Propietario de la organización (su rol no se cambia).'
                        : 'Propietario de la organización (no editable).'}
                    </p>
                  )}
                  {a.estado === 'baja' && (
                    <p className="text-[11px] text-muted-foreground mb-1">
                      Ya no es parte de este club. Reactiva a esta persona para editar sus datos o su rol.
                    </p>
                  )}
                  {a.reactivarSoloMyklubb && (
                    <p className="text-[11px] text-muted-foreground mb-1">
                      {MENSAJE_REACTIVAR_OTRA_ORG}
                    </p>
                  )}
                  {a.estado === 'desactivado' && !a.reactivarSoloMyklubb && (
                    <p className="text-[11px] text-muted-foreground mb-1">
                      Reactiva a esta persona para poder asignarle un rol.
                    </p>
                  )}
                  {a.estado === 'activo' && !member.usuario_creado && (
                    <p className="text-[11px] text-muted-foreground mb-1">
                      Completa su registro (Crear acceso) antes de cambiar el rol.
                    </p>
                  )}
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-xs font-medium text-muted-foreground">
                      Rol
                    </span>
                    {roleUpdating[member.id] ? (
                      <span className="text-[11px] text-blue-600">Actualizando...</span>
                    ) : (
                      <Select
                        value={member.rol || undefined}
                        onValueChange={(value) => handlePlatformRoleChange(member, value)}
                        disabled={!a.cambiarRol}
                      >
                        <SelectTrigger className="h-7 w-36 text-xs" aria-label={`Rol de ${member.nombre_completo}`}>
                          <SelectValue placeholder={getRoleLabel(member.rol)} />
                        </SelectTrigger>
                        <SelectContent className="text-xs">
                          {roles.map((roleOption) => (
                            <SelectItem key={roleOption.value} value={roleOption.value}>
                              {roleOption.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>
              </div>
            </Card>
            );
          })}
        </div>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onOpenChange={(open) => !open && setConfirmDialog({ open: false, staff: null, action: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {confirmDialog.action === 'create'
                ? 'Crear acceso'
                : 'Reenviar invitación'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-sm text-muted-foreground space-y-2">
              {confirmDialog.action === 'create' && (
                <>
                  <p>
                    ¿Crear el acceso de <strong>{confirmDialog.staff?.nombre_completo}</strong>?
                  </p>
                  <p>
                    Se enviará un email de invitación a: <strong>{confirmDialog.staff?.email}</strong>
                  </p>
                </>
              )}
              {confirmDialog.action === 'resend' && (
                <>
                  <p>
                    ¿Reenviar invitación a <strong>{confirmDialog.staff?.nombre_completo}</strong>?
                  </p>
                  <p>
                    Se enviará un nuevo enlace a: <strong>{confirmDialog.staff?.email}</strong>
                  </p>
                </>
              )}
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => setConfirmDialog({ open: false, staff: null, action: null })}
              >
                Cancelar
              </Button>
              <Button
                variant="default"
                onClick={handleConfirmAction}
                disabled={loading}
              >
                {confirmDialog.action === 'create'
                  ? 'Crear y Enviar'
                  : 'Reenviar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog editar staff */}
      <Dialog open={showEditForm} onOpenChange={editStaffGuard.onOpenChange}>
        <DialogContent {...editStaffGuard.dirtyProps} className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Staff</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveEdit} className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre completo *</Label>
              <Input value={editForm.nombre_completo} onChange={(e) => setEditForm((p) => ({ ...p, nombre_completo: e.target.value }))} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="staff-edit-email">Email</Label>
              <Input
                id="staff-edit-email"
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm((p) => ({ ...p, email: e.target.value }))}
                disabled={Boolean(editingStaff) && !acciones(editingStaff).correoEditable}
              />
              {editingStaff && !acciones(editingStaff).correoEditable ? (
                <p className="text-xs text-muted-foreground">{MENSAJE_CORREO_OTRA_ORG}</p>
              ) : editingStaff?.usuario_creado && editForm.email !== editingStaff?.email && (
                <p className="text-xs text-amber-600">Al cambiar el email se actualizará también la cuenta de acceso.</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Teléfono</Label>
              <Input value={editForm.telefono} onChange={(e) => setEditForm((p) => ({ ...p, telefono: e.target.value }))} placeholder="10 dígitos" />
            </div>
            {editError && (
              <p role="alert" className="text-sm text-destructive">{editError}</p>
            )}
            <div className="flex gap-2 justify-end pt-2">
              <Button type="button" variant="outline" onClick={() => editStaffGuard.onOpenChange(false)} disabled={saving}>Cancelar</Button>
              <Button type="submit" disabled={saving || !editForm.nombre_completo.trim()}>{saving ? 'Guardando...' : 'Guardar'}</Button>
            </div>
          </form>
          {editStaffGuard.DiscardDialog}
        </DialogContent>
      </Dialog>

      {/* Dialog crear staff */}
      <Dialog open={showCreateForm} onOpenChange={createStaffGuard.onOpenChange}>
        <DialogContent {...createStaffGuard.dirtyProps} className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nuevo Staff</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateStaff} className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre completo *</Label>
              <Input
                value={createForm.nombre_completo}
                onChange={(e) => setCreateForm((p) => ({ ...p, nombre_completo: e.target.value }))}
                placeholder="Ej: Juan Pérez"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm((p) => ({ ...p, email: e.target.value }))}
                placeholder="correo@ejemplo.com"
                required
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Teléfono</Label>
                <Input
                  value={createForm.telefono}
                  onChange={(e) => setCreateForm((p) => ({ ...p, telefono: e.target.value }))}
                  placeholder="10 dígitos"
                />
              </div>
              <div className="space-y-2">
                <Label>Rol</Label>
                <Select value={createForm.rol} onValueChange={(v) => { createStaffGuard.markDirty(); setCreateForm((p) => ({ ...p, rol: v })); }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {roles.map((r) => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30">
              <input
                type="checkbox"
                id="crear_auth"
                checked={createForm.crear_auth}
                onChange={(e) => setCreateForm((p) => ({ ...p, crear_auth: e.target.checked }))}
                className="rounded"
              />
              <label htmlFor="crear_auth" className="text-sm cursor-pointer">
                <span className="font-medium">Crear cuenta de acceso ahora</span>
                <p className="text-xs text-muted-foreground">Se enviará un email de invitación para que establezca su contraseña</p>
              </label>
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button type="button" variant="outline" onClick={() => createStaffGuard.onOpenChange(false)} disabled={creating}>Cancelar</Button>
              <Button type="submit" disabled={creating || !createForm.nombre_completo.trim() || !createForm.email.trim()}>
                {creating ? 'Creando...' : 'Crear Staff'}
              </Button>
            </div>
          </form>
          {createStaffGuard.DiscardDialog}
        </DialogContent>
      </Dialog>

      <PhotoUploadModal
        isOpen={showPhotoUpload}
        onClose={() => {
          setShowPhotoUpload(false);
          setSelectedStaffForPhoto(null);
          setPhotoUploadSuccess(null);
        }}
        player={selectedStaffForPhoto}
        uploading={uploadingImage === selectedStaffForPhoto?.id}
        uploadSuccess={photoUploadSuccess}
        onFileSelect={handlePhotoFileSelect}
        onCameraCapture={handlePhotoCameraCapture}
        onRemove={handleRemovePhoto}
        entityLabel="staff"
        entityName={selectedStaffForPhoto?.nombre_completo}
        imageUrl={selectedStaffForPhoto?.imagen_url}
      />
      <UpsellModal
        open={limitUpsell}
        onOpenChange={setLimitUpsell}
        feature="payments_module"
        title="Límite de staff alcanzado"
        message="Alcanzaste el límite de staff de tu plan. Mejora tu plan para agregar más."
      />
      <ConfirmDialog />
    </FeaturedLayout>
  );
};

export default StaffManagement;





